import React, {useMemo, useState} from "react";
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useNavigate, useParams} from "react-router-dom";
import {useMount, useUnmount} from "react-use";
import Opportunity, {
    acceptOpportunity,
    rejectOpportunity,
    updateOpportunity,
    withdrawOpportunity
} from "../models/Opportunity";
import AgencyOpportunity, {
    updateOpportunity as updateAgencyOpportunity,
    withdrawOpportunity as withdrawAgencyOpportunity,
    rejectOpportunity as rejectAgencyOpportunity,
} from "../models/AgencyOpportunity";
import TalentPortal from "../views/talent-portal/TalentPortal";
import {useAuth} from "./AuthProvider";
import {useAPI} from "../config/API";

let OpportunityContext = React.createContext(null);

const OpportunityProvider = ({children, model,onReset}) => {

    const queryParams = useParams();
    const oppId = model ? model.id : queryParams?.oppId;
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const {auth} = useAuth();
    const forSupplier = auth?.canManageClientResourceTalent();
    const {
        data: opportunityData,
        isFetching,
        isInitialLoading: isLoading,
        isRefetching,
        error,
        refetch
    } = useAPI([!forSupplier ? 'fetchOpportunityForTalent' : 'fetchOpportunityForAgency', parseInt(oppId)], {
            initialData: model || undefined,
            enabled: !model && !!oppId && (auth.isTalent?.() || forSupplier)
        }
    )

    const opportunity = useMemo(() => {
        return (model && !oppId) ? model : opportunityData;
    }, [model?.project_position?.proposal_generations_count,model?.project_position?.id, isFetching, isLoading, isRefetching, opportunityData]);

    const [uniqId, setUniqId] = useState(() => TB.uuid());
    const [isDisabled, setIsDisabled] = useState(false);
    useMount(() => {
        TB.event().listen("Opportunities@updated", updateCallback, uniqId)
        TB.event().listen("AgencyOpportunities@updated", updateCallback, uniqId)
    })
    useUnmount(() => {
        TB.event().provider().off("Opportunities@updated", updateCallback, uniqId)
        TB.event().provider().off("AgencyOpportunities@updated", updateCallback, uniqId)
    })

    const updateCallback = async (updatedOpp) => {
        console.log('updateCallback', updatedOpp)
        if (parseInt(updatedOpp?.id) !== parseInt(oppId)) {

            if (opportunity?.is_agency_opp && TB.find(opportunity?.matching_talent || [], ['id', updatedOpp.id])) {
                let matching_talent = TB.map(opportunity.matching_talent, talentOpp => {
                    if (updatedOpp.id === talentOpp.id) {
                        return Opportunity.make({...talentOpp,...updatedOpp})
                    }
                    return talentOpp;
                })
                return await queryClient.setQueryData(['fetchOpportunityForAgency', parseInt(oppId)], previousData => {
                    if (!previousData) return undefined;

                    return AgencyOpportunity.make({...previousData, matching_talent})
                })
            }
            return;
        }

        await queryClient.setQueryData([!forSupplier ? 'fetchOpportunityForTalent' : 'fetchOpportunityForAgency', parseInt(oppId)], previousData => {
            if (!previousData) return undefined;
            return updatedOpp?.is_agency_opp ?
                AgencyOpportunity.make({...previousData, ...updatedOpp}) :
                Opportunity.make({...previousData, ...updatedOpp})
        })
    }

    const handleOppResponse = async (response, successCallback = async () => true) => {
        console.log('handleOppResponse', response)
        if (!response) {
            TB.showServerErrorToast()
            return;
        }
        // console.log('handleOppResponse', response)
        await updateCallback(response);

        if (response?.project.is_on_hold) {
            await TB.sysAlert('talent.opportunity_disabled_project_on_hold');
            return navigate(TalentPortal.projectsListPath(), {replace: true})
        } else if (response?.status === Opportunity.Statuses.Lost) {
            await TB.sysAlert('talent.opportunity_disabled_lost');
            return navigate(response?.oppListWithSimilarStatus(), {replace: true})
        } else if (!response?.detailedViewAvailable()) {
            successCallback();

            return navigate(response?.oppListWithSimilarStatus(), {replace: true})
        }
        return successCallback();
    }
    const updateOppMutation = useMutation(['updateOpportunity', parseInt(oppId)],
        (props) => {
            if (props.opp.is_agency_opp) {
                return updateAgencyOpportunity(props.opp, props.params)
            }
            return updateOpportunity(props.opp, props.params)
        }, {
            onError: (e) => {
                console.log('error', e)
                return TB.showServerErrorToast()
            },
            onSuccess: async (updatedOpp) => {
                // console.log('onSuccess', updatedOpp)
                let didAccept = !opportunity.accepted_at && updatedOpp.accepted_at
                return handleOppResponse(updatedOpp, () => {

                    if (opportunity.is_agency_opp) {
                        if (didAccept) {
                            return TB.toast('Project Brief Accepted')
                        }else return TB.sysAlert('talent.opportunity_proposal_save_btn_click')

                    }
                })
            }
        }
    )
    const proposeJobInterest = useMutation(['proposeJobInterest', parseInt(oppId)],
        (props) => auth?.proposeInterestForJob(props.position, props.params), {
            onError: (e) => {
                return TB.showServerErrorToast()
            },
            onSuccess: async (result) => {
                // TB.toast('Showed interest for job');
                if (result.is_agency_opp) {
                    return handleOppResponse(result)
                }
            }
        }
    )

    const showJobInterest = useMutation(['showJobInterest', parseInt(oppId)],
        (props) => auth?.toggleInterestForJob(props.position, props.params), {
            onError: (e) => {
                return TB.showServerErrorToast()
            },
            onSuccess: async (result) => {
                TB.toast('Showed interest for job');
                if (!opportunity?.id && result?.id && onReset) {
                    console.log('resetting', {opportunity, result})
                    return onReset(result)
                }
                if (result.is_agency_opp) {
                    return handleOppResponse(result)
                }
            }
        }
    )
    const removeJobInterest = useMutation(['removeJobInterest', parseInt(oppId)],
        (props) => auth?.toggleInterestForJob(props.position, props.params), {
            onError: (e) => {
                return TB.showServerErrorToast()
            },
            onSuccess: async (result) => {
                TB.toast('Interest removed for job');
                if (result.is_agency_opp) {
                    return handleOppResponse(result)
                }
                if (!forSupplier && parseInt(queryParams.oppId) === opportunity.id) {
                    return navigate('/talent/projects/all', {replace: true})
                }
            }
        }
    )

    const acceptOppMutation = useMutation(['acceptOpportunity', parseInt(oppId)],
        (props) => acceptOpportunity(props.opp, props.params), {
            onError: (e) => {
                return TB.showServerErrorToast()
            },
            onSuccess: async (updatedOpp) => {

                return handleOppResponse(
                    updatedOpp,
                    async () => {
                        if (updatedOpp.is_agency_opp) {
                            return;
                        }
                        if (updatedOpp.isAwaitingTeamApproval()) {
                            return TB.sysAlert('talent.opportunity_prompt_awaiting_team_approval');
                        } else if (updatedOpp.hasSubmittedProposal()) {
                            return TB.sysAlert('talent.opportunity_accepted');
                        }
                        return TB.sysAlert("talent.pursue_job_and_begin_proposal", undefined, {
                            icon: "fas fa-thumbs-up"
                        });
                    }
                )
            }
        }
    )
    const rejectOppMutation = useMutation(['rejectOpportunity', parseInt(oppId)],
        (props) => rejectOpportunity(props.opp, props.params), {
            onError: (e) => {
                return TB.showServerErrorToast()
            },
            onSuccess: async (updatedOpp) => {
                return handleOppResponse(
                    updatedOpp,
                    async () => {
                        let shouldRejectForTeam = updatedOpp?.canAdmin()
                            && updatedOpp?.canRejectForTeam();

                        if (shouldRejectForTeam) {
                            return TB.sysAlert('talent.opportunity_rejected_for_team');
                        }
                        return TB.sysAlert('talent.opportunity_rejection_response_success');
                    }
                )
            }
        }
    )

    const withdrawOppMutation = useMutation(['withdrawOpportunity', parseInt(oppId)],
        (props) => withdrawOpportunity(props.opp, props.params), {
            onError: (e) => {
                return TB.showServerErrorToast()
            },
            onSuccess: async (updatedOpp) => {
                return handleOppResponse(
                    updatedOpp,
                    () => TB.sysAlert('talent.opportunity_rejection_response_success')
                )
            }
        }
    )
    const withdrawAgencyOppMutation = useMutation(['withdrawAgencyOpportunity', parseInt(oppId)],
        (props) => withdrawAgencyOpportunity(props.opp, props.params), {
            onError: (e) => {
                return TB.showServerErrorToast()
            },
            onSuccess: async (updatedOpp) => {
                return handleOppResponse(
                    updatedOpp,
                    () => TB.sysAlert('talent.opportunity_rejection_response_success')
                )
            }
        }
    )
    const rejectAgencyOppMutation = useMutation(['rejectAgencyOppMutation', parseInt(oppId)],
        rejectAgencyOpportunity, {
            onError: (e) => {

                return TB.showServerErrorToast()
            },
            onSuccess: async (updatedOpp) => {
                console.log('onSuccess', updatedOpp)
                return handleOppResponse(
                    updatedOpp,
                    () => TB.sysAlert('talent.opportunity_rejection_response_success')
                )
            }
        }
    )

    const onRequest = async (callback) => {
        await setIsDisabled(true);

        let result = await callback().catch(e => console.warn('ERROR: onRequest', e))

        await setIsDisabled(false);

        return result;
    }

    const onUpdate = async (model, selectedOpp) => {
        let opp = selectedOpp || opportunity

        let fields = [
            'accepted_at',
            'rejected_at',
            'rejection_reason',
            'rejection_reason_notes',
            'rejected_at',
            'withdrew_at',
            'signed_contract_at',
            // 'cover_letter',
            'latest_proposal',
            'confirmed_started_at',
            'confirmed_avg_hours_per_period',
            'accepted_for_team_at',
            'rejected_for_team_at',
            'withdrew_for_team_at',
            'hourly_rate',
            'talent_proposed_at',
            'payout_amount',
            ...TB.pluck(opp.projectBriefSteps(), 'CompletedField'),
        ]
        if (opp.is_agency_opp) {
            fields.push('signed_contract_at')
        }

        if (model.job_candidate || opp.for_agency) {
            fields.push('job_candidate')
        }


        if (opp.is_fixed_fee_contract && model.position_deliverables) {
            fields.push('position_deliverables')
        }
        if (opp.is_for_team && model.disciplines) {
            fields.push('disciplines')
        }

        let props = TB.pick(model, fields);

        props = TB.mapValues(props, prop => {
            if (TB.isDefined(prop) && prop instanceof Date) {
                prop = prop.toISOString();
            }
            return prop;
        })
        console.log('onUpdate', props)

        return await updateOppMutation.mutateAsync({opp, params: props});
    }

    const onProposeInterest = async (selectedPosition, params) => {
        console.log('OpportunityProvider @ onProposeInterest', selectedPosition, params)
        let position = selectedPosition || opportunity.project_position
        if (position.has_shown_interest_in_job) {
            TB.toast('Cannot show interest for job', {theme: 'danger'})
            return;
        }

        return proposeJobInterest.mutateAsync({position: position, params}).catch(console.warn)
    }

    const onShowInterest = async (selectedPosition, params) => {
        console.log('OpportunityProvider @ onShowInterest', selectedPosition, params)
        let position = selectedPosition || opportunity.project_position
        if (position.has_shown_interest_in_job) {
            TB.toast('Cannot show interest for job', {theme: 'danger'})
            return;
        }

        return showJobInterest.mutateAsync({position: position, params}).catch(console.warn)
    }
    const onRemoveInterest = async (selectedPosition, params) => {
        let opp = selectedPosition || opportunity.project_position
        if (!opp.has_shown_interest_in_job) {
            TB.toast('Cannot remove interest for job', {theme: 'danger'})
            return;
        }
        let confirm = await TB.alert('warning', 'Withdraw Interest?',
            'Are you sure you want to withdraw your interest for this job? Access to client and proposal will be lost',
            [{title: 'Yes, Continue'}, {title: 'Cancel'}]
        );
        if (!confirm) {
            return
        }

        return removeJobInterest.mutateAsync({position: opp, params}).catch(console.warn)
    }
    const onWithdraw = async (selectedOpp, params) => {
        let opp = selectedOpp || opportunity
        if (!opp.can_be_withdrawn) {
            TB.toast('Opportunity cannot be withdrawn', {theme: 'danger'})
            return;
        }

        return withdrawOppMutation.mutateAsync({opp, params})
    }
    const onWithdrawForAgency = async (selectedOpp, params) => {
        let opp = selectedOpp || opportunity
        if (!opp.can_be_withdrawn) {
            TB.toast('Opportunity cannot be withdrawn', {theme: 'danger'})
            return;
        }

        return withdrawAgencyOppMutation.mutateAsync({opp, params})
    }
    const onAccept = async (selectedOpp, params) => {
        let opp = selectedOpp || opportunity
        // if (!opportunity.can_be_accepted) {
        //     TB.toast('Opportunity cannot be pursured at this time', {theme: 'danger'})
        //     return;
        // }

        return acceptOppMutation.mutateAsync({opp, params})
    }
    const onReject = async (selectedOpp, params = {}) => {
        let opp = selectedOpp || opportunity
        if (!opp.can_be_rejected) {
            TB.toast('Opportunity cannot be pursued at this time', {theme: 'danger'})
            return;
        }

        return rejectOppMutation.mutateAsync({opp, params})
    }
    const onRejectForAgency = async (selectedOpp, params) => {
        let opp = selectedOpp || opportunity
        if (!opp.can_be_rejected) {
            TB.toast('Opportunity cannot be passed', {theme: 'danger'})
            return;
        }

        let requestParams = {opportunity: opp, props: params};
        return rejectAgencyOppMutation.mutate(requestParams)
    }
    let value = {
        opportunity,
        isLoading: isFetching,// && !isRefetching,
        error,
        refetch,
        changeProps: updateCallback,
        onUpdateJob: (job) => {
            if (onReset) {
                return onReset(job)
            }else return updateCallback(job)
        },
        onRequest,
        onUpdate,
        onWithdraw,
        onWithdrawForAgency,
        onShowInterest,
        onRemoveInterest,
        onProposeInterest,
        onAccept,
        onReject,
        onRejectForAgency,
        isUpdating:  updateOppMutation.isLoading ||
            acceptOppMutation.isLoading ||
            rejectOppMutation.isLoading ||
            withdrawOppMutation.isLoading ||
            removeJobInterest.isLoading ||
            showJobInterest.isLoading ||
            proposeJobInterest.isLoading ||
            withdrawAgencyOppMutation.isLoading ||
            rejectAgencyOppMutation.isLoading || showJobInterest.isLoading,
        isDisabled: isDisabled || isFetching ||
            updateOppMutation.isLoading ||
            acceptOppMutation.isLoading ||
            rejectOppMutation.isLoading ||
            withdrawOppMutation.isLoading ||
            removeJobInterest.isLoading ||
            showJobInterest.isLoading ||
            proposeJobInterest.isLoading ||
            withdrawAgencyOppMutation.isLoading ||
            rejectAgencyOppMutation.isLoading || showJobInterest.isLoading
    }

    return (
        <OpportunityContext.Provider value={value}>
            {children}
        </OpportunityContext.Provider>
    );
}

const useOpportunity = () => {
    return React.useContext(OpportunityContext);
}
export default OpportunityProvider;
export {
    useOpportunity,
    OpportunityContext
}